import { IBaseResponse } from "@nkr/models/base-response";
import { IAccountsPayable, IAccountsPayableDetail } from "@nkr/models/reports";
import { ICompBankInfo, ITransactionConfirmation } from "@nkr/models/reports/accounts-payable";
import { downloadFile } from "@nkr/services/download-file";
import { AuthActionCreators } from "@nkr/store/authorization";
import { useAppHookContext } from "@nkr/utils/rest-api/useAppHookContext";
import { useEffect, useState } from "react";
import * as Papa from 'papaparse';
import * as moment from 'moment';
import { toast } from 'react-toastify';

interface IRetVal {
    list: IAccountsPayable[],
    loading: boolean,
    error: string,
    compareBankInfo: (compBankInfos: ICompBankInfo[]) => void,
    updateAchInfo: (ap: IAccountsPayable) => void,
    exportPaymentFile: (selected: IAccountsPayable[]) => void,
    importConfirmationFile: (confirmations: ITransactionConfirmation[]) => ITransactionConfirmation[],
    markPaid: (achConfirmation: string, amountPaid: number, lineItems: IAccountsPayableDetail[]) => void
}



function useAPPayments(): IRetVal {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IAccountsPayable[]>([]);

    useEffect(() => {
        setLoading(true);
        setError('');

        const fetchData = async () => {
            var config = {
                url: `${__API_BASE_}/admin/billing-report/accounts-payable?payNowOnly=true`
            };

            try {
                const response: IBaseResponse = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.results && response.data.success) {
                    setList(response.data.results);
                } else {
                    setError('Unable to load data');
                    setList([]);
                }
            } catch (err) {
                setList([]);
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, []);

    const compareBankInfo = (compBankInfos: ICompBankInfo[]) => {
        const ccbEntries = compBankInfos.filter(c => c.vendorName.endsWith(' CCB'));

        const clone = list.map(p => {
            const n = ccbEntries.find(c => c.vendorName.slice(0, -4) === p.centerAlias.replace(/ /g, ''));
            if (n) {
                return { ...p, compAchAccountNumber: n.bankAccountNumber, compAchRoutingNumber: n.bankRoutingNumber }
            }
            return p;
        });
        setList(clone);
    }

    const updateAchInfo = async (ap: IAccountsPayable) => {
        const config = {
            url: `${__API_BASE_}/admin/billing-report/center/${ap.centerId}/ach`,
            data: {
                achRoutingNumber: ap.compAchRoutingNumber,
                achAccountNumber: ap.compAchAccountNumber
            }
        };

        try {
            const response: IBaseResponse = await dispatch(AuthActionCreators.apiPut(config));

            if (response && response.data.success) {
                const clone = list.map(p => {
                    if (p.centerId === ap.centerId) {
                        return { ...p, achRoutingNumber: ap.compAchRoutingNumber, achAccountNumber: ap.compAchAccountNumber };
                    }

                    return p;
                });

                setList(clone)
            }

        } catch (err) {
            setError(err);
        }
    }

    const exportPaymentFile = (selected: IAccountsPayable[]) => {
        const invalid = selected.filter(p => !(
            p.achRoutingNumber
            && p.achAccountNumber
            && p.compAchRoutingNumber
            && p.compAchAccountNumber
            && p.achRoutingNumber === p.compAchRoutingNumber
            && p.achAccountNumber === p.compAchAccountNumber
        ));

        if (invalid.length > 0) {
            const centers = invalid.map(p => p.centerAlias).join(', ');
            toast.error(`Bank info missing or unverified for: ${centers}`);
            return;
        }

        const now = moment();
        const delivery = moment().add(1, 'days');
        const totalCents = selected.reduce((sum, p) => sum + Math.round(p.payNow * 100), 0);
        const TRANSACTION_START = 100200;


        const headerOneColumns = [
            'Indicator',
            'File ID (Modifier)',
            'File creation date',
            'File creation time',
            'Total trxn',
            'Total ACH credit amount',
            'Total ACH debit amount',
            'Batch Count'
        ];

        const headerOneData = [
            1, // Indicator
            1, //File ID (Modifier)
            now.format('YYMMDD'), //File creation date
            now.format('HHmm'), //File creation time
            selected.length, //Total trxn
            totalCents, //Total ACH credit amount,
            0, //Total ACH debit amount
            1, //Batch Count
        ]

        const headerTwoColumns = [
            'Indicator',
            'Service class code',
            'Chase Acct',
            'SEC Code',
            'Entry description',
            'Delivery by date',
            'Batch credit amount',
            'Batch debit amount',
            'Batch number',
            'Trxn in Batch'
        ];

        const headerTwoData = [
            5, //Indicator
            220, //Service class code
            956119876, //Chase Acct
            'CCD', //SEC Code
            'CCB', //Entry description
            delivery.format('YYMMDD'), //Delivery by date
            totalCents, //Batch credit amount
            0, //Batch debit amount
            100, //Batch number
            selected.length //Trxn in Batch
        ]

        const mainColumns = [
            'Indicator',
            'Trxn Code',
            'Routing Num',
            'Acct number',
            'Trxn amount',
            'ID Number',
            'Payee name',
            'Trxn ID',
            'Addenda'
        ]

        const mainData = selected.map((pmt, i) => [
            6, //Indicator
            22, //Trxn Code
            pmt.achRoutingNumber, //Routing Num
            pmt.achAccountNumber, //Acct number
            Math.round(pmt.payNow * 100), //Trxn amount
            `${pmt.centerAlias.replace(/ /g, '')}CCB`.slice(0, 15), //ID Number
            pmt.centerAlias.replace(/ /g, ''), //Payee name
            TRANSACTION_START + i, //Trxn ID,
            `NKR CCB ${pmt.centerHasLatePaymentsInLastYear ? 'Late' : 'On Time'} Center ${delivery.format('MMMM')} ${delivery.year()}`, //Addenda
        ]);

        const rows: any[][] = [
            headerOneColumns,
            headerOneData,
            headerTwoColumns,
            headerTwoData,
            mainColumns,
            ...mainData
        ];

        downloadFile(Papa.unparse(rows), `Chase_ACH_Upload_${now.format('YYYYMMDD')}.csv`, 'text/csv');
    }

    const importConfirmationFile = (confirmations: ITransactionConfirmation[]) => {
        const recs = confirmations.reduce((acc, v) => {
            const alias = v.payTo.split(' ')[0];
            const i = acc.payments.findIndex(p => p.centerAlias.replace(/ /g, '') == alias);

            if (i < 0) {
                acc.unmatched.push(v);
            } else {
                acc.payments[i] = { ...acc.payments[i], confirmationAmount: Math.abs(v.amount), confirmationNumber: v.chaseTransactionNumber };
            }

            return acc
        }, { unmatched: [] as ITransactionConfirmation[], payments: [...list] })

        setList(recs.payments);
        return recs.unmatched;
    }


    const markPaid = (achConfirmation: string, amountPaid: number, lineItems: IAccountsPayableDetail[]) => {

        const postData = async () => {
            setLoading(true);
            var config = {
                url: `${__API_BASE_}/admin/billing-report/accounts-payable`,
                data: {
                    achConfirmation,
                    amountPaid,
                    lineItems
                }
            };

            try {

                const response: IBaseResponse = await dispatch(AuthActionCreators.apiPost(config));

                if (response && response.data.success) {
                    setList(prev => prev.filter(p => p.confirmationNumber !== achConfirmation));
                } else {
                    setError('An error occurred while trying to mark records as paid.');
                    toast.error(`An error occurred while trying to mark records as paid: ${response?.data?.message}`);
                }

            } catch (err) {
                setList([]);
                setError(err);
                toast.error(`An error occurred while trying to mark records as paid.`);
            } finally {
                setLoading(false);
            }
        }

        postData();
    }

    return { list, loading, error, compareBankInfo, updateAchInfo, exportPaymentFile, importConfirmationFile, markPaid }
}

export default useAPPayments;
